'use client';

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, BadgeCheck } from 'lucide-react';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import LoadingDots from '@/components/ui/LoadingDots/LoadingDots';

/**
 * Shared pieces of the delete and merge confirmation flows: the impact
 * preview, the typed-email gate, and the high-impact escalation step.
 *
 * Both tools ask the same two questions of the moderator, so they ask them
 * with the same components — the server re-checks every one of these
 * conditions regardless, so this is presentation, not enforcement.
 */

export interface AccountPreview {
  userId: string;
  username: string | null;
  isVerified: boolean;
  createdAt: string | null;
  publicTonesCount: number;
  publicModelsCount: number;
  downloadsCount: number;
  favoritesCount: number;
  followersCount: number;
  totalTonesCount: number;
  totalModelsCount: number;
  deletedTonesCount: number;
  commentsCount: number;
  apiKeysCount: number;
  activeTrainingsCount: number;
  toneTitles: string[];
  toneTitlesTruncated: boolean;
  requiresEscalation: boolean;
}

/** Fetch the read-only impact report for an account. Returns null until a
 * userId is supplied, so callers can hold it open for a not-yet-picked
 * merge target. */
export const useAccountPreview = (userId: string | null) => {
  const [preview, setPreview] = useState<AccountPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!userId) {
      setPreview(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/users/preview?userId=${encodeURIComponent(userId)}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load account');
      }
      setPreview(data.preview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load account');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { preview, loading, error, reload };
};

/**
 * The big number is what a delete destroys; `sub` is what the public can
 * currently see. Showing both together is the point -- a profile page counts
 * only published tones, so an account with 52 tones and 2 published reads as a
 * contradiction unless the tile says which is which.
 */
const Stat = ({ label, value, sub }: { label: string; value: number; sub?: string }) => (
  <div className="flex flex-col">
    <span className="text-white text-lg">{value.toLocaleString()}</span>
    <span className="text-zinc-500 text-xs">{label}</span>
    {sub && <span className="text-zinc-600 text-xs">{sub}</span>}
  </div>
);

export const AccountImpactSummary = ({
  preview,
  heading,
}: {
  preview: AccountPreview;
  heading?: string;
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-zinc-700 p-3">
    <div className="flex items-center gap-2">
      {heading && <span className="text-zinc-500 text-xs uppercase">{heading}</span>}
      <span className="text-white">@{preview.username ?? '(no username)'}</span>
      {preview.isVerified && <BadgeCheck className="h-4 w-4 text-blue-400" />}
    </div>
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
      <Stat
        label="tones"
        value={preview.totalTonesCount}
        sub={`${preview.publicTonesCount.toLocaleString()} published`}
      />
      <Stat
        label="models"
        value={preview.totalModelsCount}
        sub={`${preview.publicModelsCount.toLocaleString()} published`}
      />
      <Stat label="downloads" value={preview.downloadsCount} />
      <Stat label="followers" value={preview.followersCount} />
      <Stat label="comments" value={preview.commentsCount} />
      <Stat label="favorites" value={preview.favoritesCount} />
      <Stat label="API keys" value={preview.apiKeysCount} />
      <Stat label="live trainings" value={preview.activeTrainingsCount} />
    </div>
    {preview.totalTonesCount > preview.publicTonesCount && (
      <span className="text-zinc-500 text-xs">
        The profile shows {preview.publicTonesCount.toLocaleString()}. The rest are private
        {preview.deletedTonesCount > 0
          ? ` or already deleted (${preview.deletedTonesCount.toLocaleString()})`
          : ''}
        , and a delete takes them too.
      </span>
    )}
  </div>
);

export interface EmailGateState {
  email: string;
  skip: boolean;
  skipReason: string;
}

export const emptyEmailGate: EmailGateState = { email: '', skip: false, skipReason: '' };

/** True when the gate has enough input to be worth submitting. The server
 * decides whether it's actually correct. */
export const emailGateReady = (state: EmailGateState): boolean =>
  state.skip ? state.skipReason.trim().length > 0 : state.email.trim().length > 0;

export const EmailGateFields = ({
  state,
  onChange,
  subject,
  disabled,
}: {
  state: EmailGateState;
  onChange: (next: EmailGateState) => void;
  /** What the address belongs to, e.g. "this account" / "the source account". */
  subject: string;
  disabled?: boolean;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-1">
      <label className="text-zinc-400 text-sm">
        Email address on {subject}, from the request
      </label>
      <input
        type="email"
        // The address is never sent to this page: autofill would make the
        // check meaningless, and so would a browser filling it in.
        autoComplete="off"
        placeholder="name@example.com"
        className="w-full bg-zinc-900 outline-none rounded-lg border border-zinc-700 px-3 min-h-[38px] disabled:opacity-50"
        value={state.email}
        disabled={disabled || state.skip}
        onChange={(e) => onChange({ ...state, email: e.target.value })}
      />
    </div>

    <Checkbox
      label="Skip email verification"
      checked={state.skip}
      disabled={disabled}
      onChange={(checked) => onChange({ ...state, skip: checked })}
    />

    {state.skip && (
      <div className="flex flex-col gap-2 rounded-lg border border-yellow-700 bg-yellow-950 bg-opacity-30 p-3">
        <span className="flex items-start gap-2 text-yellow-300 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Without the address you have no confirmation this request came from the account
            holder. Use this only for an unreachable, spam or abuse account. The reason below is
            recorded against your name.
          </span>
        </span>
        <textarea
          rows={2}
          placeholder="Why is verification being skipped?"
          className="w-full bg-zinc-900 outline-none rounded-lg border border-zinc-700 px-3 py-2 text-sm"
          value={state.skipReason}
          disabled={disabled}
          onChange={(e) => onChange({ ...state, skipReason: e.target.value })}
        />
      </div>
    )}
  </div>
);

/**
 * The second confirmation screen for a verified account or one whose tones
 * have been downloaded more than the threshold. Shows what is about to go and
 * makes the moderator type the username back.
 */
export const EscalationFields = ({
  preview,
  typedUsername,
  onTypedUsernameChange,
  acknowledged,
  onAcknowledgedChange,
  disabled,
  consequence,
}: {
  preview: AccountPreview;
  typedUsername: string;
  onTypedUsernameChange: (next: string) => void;
  acknowledged: boolean;
  onAcknowledgedChange: (next: boolean) => void;
  disabled?: boolean;
  /** One line describing what is irreversible about this particular action. */
  consequence: string;
}) => (
  <div className="flex flex-col gap-3 rounded-lg border border-red-800 bg-red-950 bg-opacity-30 p-3">
    <span className="flex items-center gap-2 text-red-300">
      <AlertTriangle className="h-5 w-5" />
      <span className="font-semibold">High-impact account</span>
    </span>

    <span className="text-zinc-300 text-sm">
      {preview.isVerified
        ? 'This account is verified'
        : `This account's tones have ${preview.downloadsCount.toLocaleString()} downloads`}
      . {consequence}
    </span>

    {preview.toneTitles.length > 0 && (
      <div className="flex flex-col gap-1">
        <span className="text-zinc-500 text-xs uppercase">Tones</span>
        <ul className="max-h-32 overflow-y-auto text-sm text-zinc-400">
          {preview.toneTitles.map((title, i) => (
            <li key={`${title}-${i}`}>{title}</li>
          ))}
          {preview.toneTitlesTruncated && <li className="text-zinc-600">…and more</li>}
        </ul>
      </div>
    )}

    <Checkbox
      label="I have confirmed this with the account holder"
      checked={acknowledged}
      disabled={disabled}
      onChange={onAcknowledgedChange}
    />

    <div className="flex flex-col gap-1">
      <label className="text-zinc-400 text-sm">
        Type <span className="text-white">{preview.username ?? '(no username)'}</span> to confirm
      </label>
      <input
        type="text"
        autoComplete="off"
        className="w-full bg-zinc-900 outline-none rounded-lg border border-zinc-700 px-3 min-h-[38px]"
        value={typedUsername}
        disabled={disabled}
        onChange={(e) => onTypedUsernameChange(e.target.value)}
      />
    </div>
  </div>
);

export const PreviewLoading = () => (
  <div className="flex items-center gap-2 py-6 text-zinc-400">
    <LoadingDots />
    <span>Loading account…</span>
  </div>
);
